import * as Sentry from '@sentry/react'
import {
  Form,
  Image,
  Text,
  FormSubmitHandler,
  useToasts,
  Button,
} from '@lounge-fe/ui-kit'
import { useTranslation } from 'react-i18next'
import {
  ProspectFormSectionData,
  prospectFormSchema,
  ProspectFormValues,
} from './types'
import { CareersService } from '../../services/CareersService'
import { useState, useMemo } from 'react'
import classNames from 'classnames'
import { useJobs } from '../../hooks/useJobs'
import { ShopifySectionComponent } from '@/types/ShopifySectionComponent'

export const ProspectForm: ShopifySectionComponent<ProspectFormSectionData> = ({
  data,
}) => {
  const [submitSuccess, setSubmitSuccess] = useState(false)
  const { t } = useTranslation()
  const { toast } = useToasts()

  if (!data) return null

  const {
    title,
    subtitle,
    image,
    departments: departmentConfig,
    successTitle,
    successSubtitle,
    successButtonText,
    className,
  } = data

  const filterDepartments = useMemo(() => {
    if (!departmentConfig.length) return []
    return departmentConfig.flatMap((dept) => dept.greenhouse_ids.map(Number))
  }, [departmentConfig])

  const { data: jobsData } = useJobs({
    targetPageSize: 1,
    filterDepartments,
    filterLocations: [],
    articles: [],
  })

  const departments = jobsData?.departments || []

  const defaultDepartmentId = useMemo(() => {
    if (departmentConfig.length > 0) {
      return departmentConfig[0]?.greenhouse_ids?.[0] || ''
    }
    return ''
  }, [departmentConfig])

  const defaultValues: ProspectFormValues = {
    firstName: '',
    lastName: '',
    email: '',
    resume: [],
    department: defaultDepartmentId,
  }

  const onSubmit: FormSubmitHandler<ProspectFormValues> = async ({
    firstName,
    lastName,
    email,
    resume,
    department,
  }) => {
    try {
      const careers = new CareersService()
      await careers.submitProspect({
        first_name: firstName,
        last_name: lastName,
        email,
        resume: [resume[0]],
        department_id: parseInt(department, 10),
      })
      setSubmitSuccess(true)
    } catch (error) {
      Sentry.captureException(error)
      toast({
        severity: 'critical',
        title: t('general.error'),
        message: t('general.error_unexpected'),
      })
    }
  }

  const handleClick = (event: React.MouseEvent) => {
    event.stopPropagation()
    document.getElementById('section-careers-vacancies')?.scrollIntoView()
  }

  return (
    <div className="wrapper">
      <div
        className={classNames(
          'flex flex-col md:flex-row items-stretch',
          className,
        )}
        id="prospect-form"
      >
        {image && (
          <div className="overflow-hidden md:basis-2/5 flex-shrink-0">
            <Image
              className="object-cover h-full w-full"
              data={{ src: image.src, width: 675, height: 675, crop: 'center' }}
              alt={image.alt}
            />
          </div>
        )}
        <div
          className={classNames(
            'px-6 py-8 bg-primary md:p-12 border border-primary md:border-t flex flex-col justify-center w-full',
            image && 'border-t-0 md:border-l-0 md:basis-3/5 min-h-[750px]',
          )}
        >
          {submitSuccess ? (
            <div className="w-full h-full flex flex-col gap-8 justify-center items-center">
              <div className="flex flex-col gap-6">
                <Text
                  variant="display"
                  className="typography--title-three italic font-light"
                >
                  {successTitle}
                </Text>
                <Text variant="body-md">{successSubtitle}</Text>
              </div>
              <Button onClick={handleClick}>{successButtonText}</Button>
            </div>
          ) : (
            <div className="flex flex-col gap-6">
              <Text
                variant="display"
                className="typography--title-three italic font-light"
              >
                {title}
              </Text>
              {subtitle && (
                <Text variant="body-md" className="md:mb-2">
                  {subtitle}
                </Text>
              )}
              <Form
                onSubmit={onSubmit}
                defaultValues={defaultValues}
                className="flex flex-col h-full"
                validationSchema={prospectFormSchema}
              >
                <div className="grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-6">
                  <Form.Input
                    name="firstName"
                    placeholder={t('account.first_name')}
                    filled
                  />
                  <Form.Input
                    name="lastName"
                    placeholder={t('account.last_name')}
                    filled
                  />
                  <Form.Input
                    name="email"
                    placeholder={t('account.email_address')}
                    filled
                  />
                  <Form.Select
                    name="department"
                    placeholder="Select department"
                    options={departments.map((department) => ({
                      label: department.name,
                      value: department.id.toString(),
                    }))}
                  />
                </div>
                <div className="flex flex-col gap-3 mt-6 mb-8">
                  <Text variant="body-sm">Upload your CV:</Text>
                  <Form.FileUpload
                    name="resume"
                    accept={{ 'application/pdf': ['.pdf'] }}
                    maxSize={1024 * 1024 * 5 /* 5MB */}
                  />
                </div>
                <Form.Submit className="md:mt-4 self-end w-full" size="lg">
                  {t('action.submit')}
                </Form.Submit>
              </Form>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
